import { doc, updateDoc, addDoc, collection } from "firebase/firestore";
import { db } from "../lib/firebase";
import { Opportunity, RebidTracking, UserProfile, StageHistoryEntry } from "../types";
import { sanitizeForFirestore } from "../utils/firestoreUtils";

export interface CalculateRebidDatesParams {
  contractStartDate: string; // YYYY-MM-DD
  baseYears: number; // e.g. 1, 2, 3, 5
  optionYears?: number; // e.g. 0, 1, 2
  noticeLeadMonths?: number; // default 6 months
}

export interface CalculatedRebidDates {
  contractEndDate: string;
  maxExpirationDate: string;
  rebidCaptureNoticeDate: string;
}

/**
 * Calculates contract end date, max expiration date with option renewals,
 * and the pre-rebid capture planning alert date.
 */
export function calculateRebidDates({
  contractStartDate,
  baseYears,
  optionYears = 0,
  noticeLeadMonths = 6,
}: CalculateRebidDatesParams): CalculatedRebidDates {
  const [yearStr, monthStr, dayStr] = contractStartDate.split("-");
  const year = parseInt(yearStr || "2026", 10);
  const month = parseInt(monthStr || "1", 10) - 1; // 0-indexed
  const day = parseInt(dayStr || "1", 10);

  const start = new Date(year, month, day);

  // Contract base end date
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + Math.max(1, baseYears));
  // Subtract 1 day for standard contract terms (e.g. Nov 1, 2026 - Oct 31, 2028)
  end.setDate(end.getDate() - 1);

  // Max expiration date including option periods
  const maxExp = new Date(start);
  maxExp.setFullYear(maxExp.getFullYear() + Math.max(1, baseYears + Math.max(0, optionYears)));
  maxExp.setDate(maxExp.getDate() - 1);

  // Rebid capture notice date (e.g. 6 months before max expiration date)
  const noticeDate = new Date(maxExp);
  const targetDay = noticeDate.getDate();
  const targetMonth = noticeDate.getMonth() - Math.max(1, noticeLeadMonths);
  noticeDate.setDate(1);
  noticeDate.setMonth(targetMonth);
  const lastDay = new Date(noticeDate.getFullYear(), noticeDate.getMonth() + 1, 0).getDate();
  noticeDate.setDate(Math.min(targetDay, lastDay));

  const format = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dayOfMonth = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dayOfMonth}`;
  };

  return {
    contractEndDate: format(end),
    maxExpirationDate: format(maxExp),
    rebidCaptureNoticeDate: format(noticeDate),
  };
}

/**
 * Returns number of calendar days until the rebid capture alert date.
 * Returns null if no notice date is configured.
 */
export function getDaysUntilRebidNotice(rebidCaptureNoticeDate?: string): number | null {
  if (!rebidCaptureNoticeDate) return null;
  const target = new Date(rebidCaptureNoticeDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diffMs = target.getTime() - today.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Saves rebid tracking details on an opportunity document.
 */
export async function saveRebidTracking(
  opportunityId: string,
  tracking: RebidTracking,
): Promise<void> {
  const now = new Date().toISOString();
  const oppRef = doc(db, "opportunities", opportunityId);

  await updateDoc(
    oppRef,
    sanitizeForFirestore({
      rebidTracking: tracking,
      "postMortem.rebidTracking": tracking,
      updatedAt: now,
    }),
  );
}

export interface CloneForRebidParams {
  originalOpportunity: Opportunity;
  user: UserProfile;
  newDueDate: string;
  rebidNotes?: string;
}

/**
 * Initializes a new Opportunity in Stage 1 (Intake) linked to the original past bid.
 */
export async function cloneOpportunityForRebid({
  originalOpportunity,
  user,
  newDueDate,
  rebidNotes,
}: CloneForRebidParams): Promise<string> {
  const now = new Date().toISOString();
  const userName = user.displayName || user.email;

  const baseTitle = originalOpportunity.title.startsWith("[Rebid]")
    ? originalOpportunity.title
    : `[Rebid] ${originalOpportunity.title}`;

  const initialHistory: StageHistoryEntry = {
    stage: "reviewing",
    changedAt: now,
    changedBy: userName,
    changedByEmail: user.email,
    note: `Initialized as Rebid from past opportunity: ${originalOpportunity.title} (ID: ${originalOpportunity.id})`,
  };

  const newOppPayload: Record<string, unknown> = {
    title: baseTitle,
    client: originalOpportunity.client,
    services: originalOpportunity.services || [],
    dueDate: newDueDate,
    stage: "reviewing",
    owner: userName,
    ownerEmail: user.email,
    originalOpportunityId: originalOpportunity.id,
    outcomeNotes: rebidNotes || undefined,
    stageHistory: [initialHistory],
    documentPaths: [],
    createdAt: now,
    updatedAt: now,
  };

  const collRef = collection(db, "opportunities");
  const docRef = await addDoc(collRef, sanitizeForFirestore(newOppPayload));
  const newOppId = docRef.id;

  // Update original opportunity's rebidTracking with the linked new opportunity ID
  const existingTracking = originalOpportunity.rebidTracking || { isRebidTracked: true };
  const updatedTracking: RebidTracking = {
    ...existingTracking,
    isRebidTracked: true,
    rebidOpportunityId: newOppId,
  };

  await saveRebidTracking(originalOpportunity.id, updatedTracking);

  return newOppId;
}
